import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Users, Phone, IdCard, Wallet, Upload, Loader2, CheckCircle2, Clock, Home as HomeIcon, Copy, Lock, MessageCircle, Download, Camera } from "lucide-react";
import html2canvas from "html2canvas";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { REGISTRATION_FORM } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HARGA_PER_ORANG = 50000;
const POSTER_URL =
  "https://customer-assets-v7afamib.emergentagent.net/job_5e118d06-4ae9-4ffe-9ea6-7ff5dab52f9f/artifacts/ou5km9a6_WhatsApp%20Image%202026-09-21%20at%2019.49.03.jpeg";

const formatRupiah = (n) => `Rp${n.toLocaleString("id-ID")}`;
const ADMIN_WHATSAPP = "6285710981671";
const buildWaLink = (text) => `https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent(text)}`;

function WhatsAppAdminButton({ testid, text }) {
  return (
    <a
      data-testid={testid}
      href={buildWaLink(text)}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-full px-5 py-3 text-sm transition-colors"
    >
      <MessageCircle className="w-4 h-4" />
      Tanya Admin via WhatsApp
    </a>
  );
}

export default function RegistrationForm() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const successCardRef = useRef(null);

  const [checkingSettings, setCheckingSettings] = useState(true);
  const [registrasiDibuka, setRegistrasiDibuka] = useState(true);

  const [nama, setNama] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [bpkel, setBpkel] = useState("");
  const [bawaKeluarga, setBawaKeluarga] = useState("tidak");
  const [jumlahKeluarga, setJumlahKeluarga] = useState(1);
  const [anggotaKeluarga, setAnggotaKeluarga] = useState("");
  const [buktiFile, setBuktiFile] = useState(null);
  const [buktiPreview, setBuktiPreview] = useState(null);
  const [uploadMode, setUploadMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((res) => setRegistrasiDibuka(res.data.registrasi_dibuka))
      .catch(() => setRegistrasiDibuka(true))
      .finally(() => setCheckingSettings(false));
  }, []);

  const jumlahPeserta = useMemo(() => {
    return bawaKeluarga === "ya" ? 1 + (parseInt(jumlahKeluarga, 10) || 0) : 1;
  }, [bawaKeluarga, jumlahKeluarga]);

  const totalBayar = jumlahPeserta * HARGA_PER_ORANG;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      toast.error("Bukti transfer harus berupa gambar JPG/PNG");
      return;
    }
    setBuktiFile(file);
    setBuktiPreview(URL.createObjectURL(file));
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("nama", nama);
    formData.append("whatsapp", whatsapp);
    formData.append("bpkel", bpkel);
    formData.append("bawa_keluarga", bawaKeluarga);
    formData.append("jumlah_keluarga", bawaKeluarga === "ya" ? jumlahKeluarga : 0);
    formData.append("anggota_keluarga", anggotaKeluarga);
    return formData;
  };

  const validateBaseFields = () => {
    if (!nama || !whatsapp) {
      setError("Mohon lengkapi Nama dan No. WhatsApp.");
      return false;
    }
    if (bawaKeluarga === "ya" && (!jumlahKeluarga || jumlahKeluarga < 1)) {
      setError("Mohon isi jumlah keluarga yang ikut.");
      return false;
    }
    return true;
  };

  const handlePayLater = async () => {
    setError("");
    if (!validateBaseFields()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/registrations`, buildFormData(), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccessData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal mengirim pendaftaran. Coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitWithProof = async (e) => {
    e.preventDefault();
    setError("");
    if (!validateBaseFields()) return;
    if (!buktiFile) {
      setError("Mohon upload bukti transfer.");
      return;
    }
    const formData = buildFormData();
    formData.append("bukti_transfer", buktiFile);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/registrations`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccessData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal mengirim pendaftaran. Coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadCard = async () => {
    if (!successCardRef.current) return;
    try {
      const canvas = await html2canvas(successCardRef.current, {
        backgroundColor: "#18181b",
        scale: 2,
      });
      const link = document.createElement("a");
      link.download = `Bukti-Pendaftaran-${successData.no_pendaftaran}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Gambar berhasil didownload!");
    } catch (err) {
      toast.error("Gagal download gambar, coba screenshot manual.");
    }
  };

  if (checkingSettings) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!registrasiDibuka && !successData) {
    return (
      <div
        data-testid={REGISTRATION_FORM.closedNotice}
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
      >
        <div className="max-w-md text-center bg-zinc-900/80 border border-orange-500/30 rounded-2xl p-8">
          <Lock className="w-12 h-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Pendaftaran Baru Sudah Ditutup</h2>
          <p className="text-white/60 text-sm mb-6">
            Pendaftaran untuk Jambore V sudah ditutup. Jika Anda sudah mendaftar sebelumnya dan belum upload bukti
            transfer, silakan lanjutkan di halaman upload.
          </p>
          <Link
            data-testid={REGISTRATION_FORM.closedUploadLink}
            to="/barat/upload"
            className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 rounded-full font-bold px-6 py-3 text-sm transition-colors"
          >
            Upload Bukti Transfer
          </Link>
        </div>
      </div>
    );
  }

  if (successData) {
    const belumUpload = !successData.bukti_transfer_path;
    return (
      <div
        data-testid={REGISTRATION_FORM.successPage}
        className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-16"
      >
        <div className="max-w-md w-full text-center bg-zinc-900/80 border border-orange-500/30 rounded-2xl p-8 shadow-[0_0_50px_rgba(234,88,12,0.15)]">
          <div
            data-testid={REGISTRATION_FORM.downloadInstructionBanner}
            className="flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 mb-6 text-xs text-white/60"
          >
            <Camera className="w-3.5 h-3.5 text-orange-400 shrink-0" />
            Screenshot atau download kartu ini sebagai bukti pendaftaran Anda!
          </div>

          <div ref={successCardRef} className="bg-zinc-900/80 rounded-xl">
            <CheckCircle2 className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Anton', sans-serif" }}>
              PENDAFTARAN BERHASIL!
            </h2>
            <p className="text-white/60 text-sm mb-4">Terima kasih, {successData.nama}. Simpan nomor pendaftaran ini.</p>

            <div className="bg-black/40 border border-white/10 rounded-xl p-4 mb-4">
              <p className="text-xs text-white/40 uppercase tracking-widest mb-1">No. Pendaftaran</p>
              <div className="flex items-center justify-center gap-2">
                <span
                  data-testid={REGISTRATION_FORM.successNoPendaftaran}
                  className="text-2xl font-bold text-orange-400 tracking-wider"
                >
                  {successData.no_pendaftaran}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(successData.no_pendaftaran);
                    toast.success("Nomor pendaftaran disalin!");
                  }}
                  className="text-white/50 hover:text-orange-400 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div
              data-testid={REGISTRATION_FORM.successStatusBadge}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold mb-4 ${
                belumUpload ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
              }`}
            >
              {belumUpload ? <Clock className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              {belumUpload ? "Menunggu Pembayaran" : "Menunggu Verifikasi Admin"}
            </div>

            <div className="text-left space-y-2 text-sm text-white/70 mb-2">
              <div className="flex justify-between">
                <span>Jumlah Peserta</span>
                <span className="font-semibold text-white">{successData.jumlah_peserta} orang</span>
              </div>
              <div className="flex justify-between">
                <span>Total Bayar</span>
                <span className="font-semibold text-orange-400">{formatRupiah(successData.total_bayar)}</span>
              </div>
            </div>
          </div>

          <Button
            data-testid={REGISTRATION_FORM.downloadCardButton}
            type="button"
            onClick={handleDownloadCard}
            variant="outline"
            className="w-full border-orange-500/40 text-orange-400 hover:bg-orange-500/10 rounded-full font-bold mt-4 mb-6"
          >
            <Download className="w-4 h-4 mr-2" />
            Download sebagai Gambar
          </Button>

          {belumUpload && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-6 text-left text-sm text-white/70">
              Setelah transfer, upload bukti bayar Anda kapan saja di halaman{" "}
              <Link
                data-testid={REGISTRATION_FORM.successUploadLink}
                to={`/barat/upload?no=${successData.no_pendaftaran}`}
                className="text-orange-400 underline font-semibold"
              >
                Upload Bukti Transfer
              </Link>{" "}
              menggunakan No. Pendaftaran & No. WhatsApp Anda.
            </div>
          )}

          <div className="mb-3">
            <WhatsAppAdminButton
              testid={REGISTRATION_FORM.whatsappAdminButtonSuccess}
              text={`Halo Admin, saya ${successData.nama} (No. Pendaftaran ${successData.no_pendaftaran}) ingin bertanya / ada masalah transfer.`}
            />
          </div>

          <Button
            data-testid={REGISTRATION_FORM.successBackHomeButton}
            onClick={() => navigate("/")}
            className="w-full bg-orange-600 hover:bg-orange-500 rounded-full font-bold"
          >
            <HomeIcon className="w-4 h-4 mr-2" />
            Kembali ke Beranda
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={REGISTRATION_FORM.page} className="min-h-screen bg-black text-white">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <img
          data-testid={REGISTRATION_FORM.posterImage}
          src={POSTER_URL}
          alt="Poster Jambore V BPK Oi Jakarta Barat"
          className="w-full rounded-2xl border border-orange-500/20 shadow-[0_0_40px_rgba(234,88,12,0.15)] mb-8"
        />

        <form onSubmit={handleSubmitWithProof} className="space-y-6">
          <div className="space-y-2">
            <Label className="text-white/90 flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-400" /> Nama Peserta/Pendaftar
            </Label>
            <Input
              data-testid={REGISTRATION_FORM.namaInput}
              value={nama}
              onChange={(e) => setNama(e.target.value)}
              placeholder="Nama lengkap"
              required
              className="bg-zinc-900 border-white/15 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/90 flex items-center gap-2">
              <Phone className="w-4 h-4 text-orange-400" /> No. WhatsApp
            </Label>
            <Input
              data-testid={REGISTRATION_FORM.whatsappInput}
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="08xxxxxxxxxx"
              required
              className="bg-zinc-900 border-white/15 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/90 flex items-center gap-2">
              <IdCard className="w-4 h-4 text-orange-400" /> BPKel (opsional)
            </Label>
            <Input
              data-testid={REGISTRATION_FORM.bpkelInput}
              value={bpkel}
              onChange={(e) => setBpkel(e.target.value)}
              placeholder="Nama BPKel"
              className="bg-zinc-900 border-white/15 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-white/90">Apakah membawa keluarga?</Label>
            <RadioGroup
              data-testid={REGISTRATION_FORM.bawaKeluargaRadioGroup}
              value={bawaKeluarga}
              onValueChange={setBawaKeluarga}
              className="grid grid-cols-2 gap-3"
            >
              {[
                { value: "tidak", label: "Tidak", testid: REGISTRATION_FORM.bawaKeluargaOptionTidak },
                { value: "ya", label: "Ya", testid: REGISTRATION_FORM.bawaKeluargaOptionYa },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                    bawaKeluarga === opt.value ? "border-orange-500 bg-orange-500/10" : "border-white/10 bg-zinc-900"
                  }`}
                >
                  <RadioGroupItem data-testid={opt.testid} value={opt.value} id={opt.value} />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          {bawaKeluarga === "ya" && (
            <>
              <div className="space-y-2">
                <Label className="text-white/90">Berapa orang keluarga yang ikut?</Label>
                <Input
                  data-testid={REGISTRATION_FORM.jumlahKeluargaInput}
                  type="number"
                  min={1}
                  value={jumlahKeluarga}
                  onChange={(e) => setJumlahKeluarga(e.target.value)}
                  placeholder="Contoh: 2"
                  className="bg-zinc-900 border-white/15 text-white placeholder:text-white/30"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/90">Nama anggota keluarga (opsional)</Label>
                <Textarea
                  data-testid={REGISTRATION_FORM.anggotaKeluargaTextarea}
                  value={anggotaKeluarga}
                  onChange={(e) => setAnggotaKeluarga(e.target.value)}
                  placeholder={"Nama + hubungan\nContoh:\nNur Azizah — Istri\nAhmad — Anak"}
                  rows={3}
                  className="bg-zinc-900 border-white/15 text-white placeholder:text-white/30"
                />
              </div>
            </>
          )}

          <div
            data-testid={REGISTRATION_FORM.totalPesertaDisplay}
            className="flex items-center gap-2 bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm text-white/70"
          >
            <Users className="w-4 h-4 text-orange-400" />
            Total peserta: <span className="font-bold text-white">{jumlahPeserta} orang</span>
          </div>

          <div className="bg-zinc-900/60 border border-white/10 rounded-2xl p-5 space-y-3">
            <p className="text-orange-400 font-semibold text-sm">💳 Pembayaran: Rp50.000 × jumlah peserta</p>
            <div
              data-testid={REGISTRATION_FORM.totalBayarDisplay}
              className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-xl px-4 py-3"
            >
              <Wallet className="w-4 h-4 text-orange-400 shrink-0" />
              <span className="text-sm text-white/80">
                {jumlahPeserta} orang × Rp50.000 = <span className="text-orange-400 font-bold">{formatRupiah(totalBayar)}</span>
              </span>
            </div>

            <div className="bg-black/40 border border-white/10 rounded-xl p-4">
              <p className="text-white/50 text-xs uppercase tracking-wide mb-2">Transfer ke</p>
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className="text-2xl sm:text-3xl font-bold text-white tracking-wider">3430871101</span>
                <button
                  type="button"
                  data-testid={REGISTRATION_FORM.copyRekeningButton}
                  onClick={() => {
                    navigator.clipboard.writeText("3430871101");
                    toast.success("Nomor rekening disalin!");
                  }}
                  className="shrink-0 flex items-center gap-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-full px-3 py-2 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" /> Salin
                </button>
              </div>
              <p className="text-lg font-semibold text-white/90">BCA a.n. Mohamad Tarono</p>
            </div>

            <WhatsAppAdminButton
              testid={REGISTRATION_FORM.whatsappAdminButton}
              text="Halo Admin, saya ingin bertanya tentang pendaftaran Jambore V / ada masalah transfer."
            />
          </div>

          {error && (
            <div data-testid={REGISTRATION_FORM.errorAlert} className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {!uploadMode ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <Button
                data-testid={REGISTRATION_FORM.payNowButton}
                type="button"
                onClick={() => setUploadMode(true)}
                className="w-full bg-orange-600 hover:bg-orange-500 rounded-full font-bold py-6"
              >
                Saya Sudah Transfer — Upload Sekarang
              </Button>
              <Button
                data-testid={REGISTRATION_FORM.payLaterButton}
                type="button"
                disabled={submitting}
                onClick={handlePayLater}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10 rounded-full font-bold py-6"
              >
                {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
                Transfer Nanti — Daftar Dulu Aja
              </Button>
            </div>
          ) : (
            <div className="space-y-4 border border-orange-500/30 bg-orange-500/5 rounded-2xl p-4">
              <div className="space-y-2">
                <Label className="text-white/90 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-orange-400" /> Bukti Transfer (JPG/PNG)
                </Label>
                <input
                  data-testid={REGISTRATION_FORM.buktiTransferInput}
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-orange-600 file:text-white file:font-semibold hover:file:bg-orange-500 file:cursor-pointer cursor-pointer bg-zinc-900 border border-white/15 rounded-xl"
                />
                {buktiPreview && (
                  <img
                    data-testid={REGISTRATION_FORM.buktiTransferPreview}
                    src={buktiPreview}
                    alt="Preview bukti transfer"
                    className="mt-2 max-h-48 rounded-xl border border-white/10"
                  />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  data-testid={REGISTRATION_FORM.submitButton}
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-orange-600 hover:bg-orange-500 rounded-full font-bold py-6"
                >
                  {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
                  Kirim Sekarang
                </Button>
                <Button
                  data-testid={REGISTRATION_FORM.cancelUploadModeButton}
                  type="button"
                  variant="outline"
                  onClick={() => setUploadMode(false)}
                  className="w-full border-white/20 text-white hover:bg-white/10 rounded-full font-bold py-6"
                >
                  Batal
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
